import { Component, OnInit, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from 'src/app/core/services/user/user.service';
import { Group, GroupMembership, User } from 'src/app/shared/models/interfaces';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { Subscription } from 'rxjs';
import {
  notOnlyLetters,
  updateErrorMessages,
  checkIfPasswordsMatch,
} from 'src/app/shared/models/validators';
import { ChangeDetectorRef } from '@angular/core';
import { AuthService } from 'src/app/core/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/ui-components/confirm-dialog/confirm-dialog.component';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DATE_FORMATS } from 'src/app/shared/constants/date-formats';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS}]
})
export class ProfileComponent implements OnInit, OnDestroy {
  groupCodeForm: FormGroup;
  profileForm: FormGroup;

  /* Deprecated JWT authentication - commented out for potential future reintegration.
  passwordForm: FormGroup;
  fieldErrors = signal<{ [key: string]: string }>({});
  passwordErrors = signal<{ [key: string]: string }>({}); */

  groupMemberships: GroupMembership[] = [];
  isOidcUser: boolean = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private snackBarService: SnackbarService,
    private cdr: ChangeDetectorRef,
    readonly authService: AuthService,
    private dialog: MatDialog
  ) {
    // Form for joining a group by code
    this.groupCodeForm = this.fb.group({
      groupCode: ['', [Validators.required]],
    });

    this.profileForm = this.fb.group({
      email: [{ value: '' }, [Validators.required, Validators.email]],
      firstName: [
        '',
        [Validators.required, Validators.minLength(2), notOnlyLetters()],
      ],
      lastName: [
        '',
        [Validators.required, Validators.minLength(2), notOnlyLetters()],
      ],
    });

    // Disable the profile form for OIDC users
    this.profileForm.disable();

    /* Deprecated JWT authentication - commented out for potential future reintegration.

    // Separate form for password updates
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required, checkIfPasswordsMatch()]],
    });

    

    // Trigger validation for confirmPassword when password changes
    this.passwordForm.get('password')?.valueChanges.subscribe(() => {
      this.passwordForm.get('confirmPassword')?.updateValueAndValidity();
    }); */
  }

  ngOnInit(): void {
    // Check if the user is an OIDC user
    this.subscriptions.push(
      this.authService.isUserOidc().subscribe((isOidc) => {
        this.isOidcUser = isOidc;
      })
    );
    // Fetch user data from the backend and populate the form
    this.userService.getProfile().subscribe({
      next: (user: User) => {
        const { password, ...userData } = user; // Exclude password fields
        this.profileForm.patchValue(userData);
      },
      error: () => {
        this.snackBarService.showErrorMessage(
          'Failed to load user profile. Please log in again.'
        );
      },
    });

    // Fetch user's groups
    this.getGroupsForUser();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onDeleteProfile(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Profile',
        message:
          'Are you sure you want to delete your profile? This action cannot be undone.',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.authService.deleteCurrentUser().subscribe({
          next: () => {
            this.snackBarService.showSuccessMessage(
              'Profile deleted successfully!'
            );
          },
          error: (err: any) => {
            this.snackBarService.showErrorMessage(
              'Failed to delete profile. Please try again.'
            );
          },
        });
      } else {
        this.snackBarService.showSnackBar(
          'info',
          'Profile deletion cancelled.'
        );
      }
    });
  }

  onSubmitGroupCode(): void {
    if (this.groupCodeForm.valid) {
      const { groupCode } = this.groupCodeForm.value;
      this.userService.joinGroup(groupCode).subscribe({
        next: (groupMembership: GroupMembership) => {
          this.snackBarService.showSuccessMessage(`You joined the group: '${groupMembership.groups.groupName}'!`);
          // Optionally, refresh group list
          this.getGroupsForUser();
          this.groupCodeForm.reset();
          this.groupCodeForm.get('groupCode')?.setErrors(null);
        },
        error: () => {
          this.snackBarService.showErrorMessage(
            'Failed to join group. Please check the group code and try again.'
          );
        },
      });
    }
  }

  onLeaveGroup(groupId: number): void {
    this.userService.leaveGroup(groupId).subscribe({
      next: () => {
        this.snackBarService.showSuccessMessage('Left group successfully!');
        // Refresh group list
        this.userService.getGroupsForUser().subscribe({
          next: (gm: GroupMembership[]) => {
            this.groupMemberships = gm;
            this.cdr.detectChanges(); // force change detection
          },
        });
      },
      error: () => {
        this.snackBarService.showErrorMessage(
          'Failed to leave group. Please try again.'
        );
      },
    });
  }

  onSetFavoriteGroup(groupId: number): void {
    const isAlreadyFavorite = this.groupMemberships.find((gm) => gm.isFavorite && gm.groups.id === groupId);
    if (isAlreadyFavorite) {
      this.snackBarService.showDefaultMessage('This group is already your favorite.');
      return;
    }
    this.userService.setFavoriteGroup(groupId).subscribe({
      next: (groupMemberships: GroupMembership[]) => {
        this.snackBarService.showSuccessMessage('New favorite group set successfully!');
        this.getGroupsForUser();
      },
      error: () => {
        this.snackBarService.showErrorMessage('Failed to set favorite group. Please try again.');
      },
    });
  }
  
  private getGroupsForUser() {
    this.userService.getGroupsForUser().subscribe({
      next: (groupMemberships: GroupMembership[]) => {
        this.groupMemberships = groupMemberships;
      },
      error: () => {
        this.snackBarService.showErrorMessage('Failed to load groups, please refresh the page.');
      },
    });
  }

  get favoriteGroupMembership(): GroupMembership | undefined {
    const favoriteGroup = this.groupMemberships.find((gm) => gm.isFavorite);
    return favoriteGroup;
  }

  /* Deprecated JWT authentication - commented out for potential future reintegration.
  updateProfileErrorMessages(): void {
    updateErrorMessages(this.profileForm, this.fieldErrors);
  }

  updatePasswordErrorMessages(): void {
    updateErrorMessages(this.passwordForm, this.passwordErrors);
  }


  onSubmitPassword(): void {
    if (this.passwordForm.valid) {
      const { password } = this.passwordForm.value;
      const updateData = { password };

      this.userService.updateProfile(updateData).subscribe({
        next: () => {
          this.snackBarService.showSuccessMessage(
            'Password updated successfully!'
          );
          this.passwordForm.markAsPristine();
          this.passwordForm.markAsUntouched();
          this.passwordForm.reset();
          this.passwordErrors.set({}); // Reset password errors
        },
        error: () => {
          this.snackBarService.showErrorMessage(
            'Failed to update password. Please try again.'
          );
        },
      });
    }
  }

  onSubmitProfile(): void {
    if (this.profileForm.valid) {
      const { email, firstName, lastName } = this.profileForm.value;
      const updateData = { email, firstName, lastName };

      this.userService.updateProfile(updateData).subscribe({
        next: () => {
          this.snackBarService.showSuccessMessage(
            'Profile updated successfully!'
          );
        },
        error: (err: any) => {
          this.snackBarService.showErrorMessage(
            'Failed to update profile. Please try again.'
          );
        },
      });
    }
  } */
}
